import React, { useCallback, useRef } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Slider, Stack, Switch, Typography } from '@mui/material';
import type { CropRect } from '../floorplan/floorPlanWalls';

export interface NorthCalibrationDialogProps {
  open: boolean;
  imageUrl: string;
  title?: string;
  description?: string;
  /** Degrees clockwise from the top of the image as displayed. */
  value: number;
  onChange: (value: number) => void;
  /** When provided, a Mirror switch is shown and the preview is flipped horizontally. */
  mirror?: boolean;
  onMirrorChange?: (value: boolean) => void;
  /** When provided, a draggable crop rectangle (percent of the image) is shown. */
  crop?: CropRect | null;
  onCropChange?: (crop: CropRect) => void;
  saving: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  /** Extra content under the slider (scope pickers, overrides, previews). */
  children?: React.ReactNode;
  /** Content rendered beside the image (e.g. a live 3D preview). */
  aside?: React.ReactNode;
  maxWidth?: 'md' | 'lg' | 'xl';
}

type Handle = 'nw' | 'ne' | 'sw' | 'se' | 'move';
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * "Align north with the printed compass": rotate an arrow over the image until
 * it matches the sheet's N. Optionally flips the preview (mirror) and edits a
 * crop rectangle, both used by floor-plan calibration.
 */
export default function NorthCalibrationDialog(props: NorthCalibrationDialogProps) {
  const { open, imageUrl, value, mirror, crop, saving, error, onCropChange } = props;
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ handle: Handle; startX: number; startY: number; start: CropRect } | null>(null);

  const onPointerDown = useCallback((handle: Handle) => (event: React.PointerEvent) => {
    if (!crop || !onCropChange) return;
    event.preventDefault(); event.stopPropagation();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    drag.current = { handle, startX: event.clientX, startY: event.clientY, start: crop };
  }, [crop, onCropChange]);
  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const d = drag.current; const rect = frame.current?.getBoundingClientRect();
    if (!d || !rect || !onCropChange) return;
    const dx = (event.clientX - d.startX) / rect.width * 100 * (mirror ? -1 : 1);
    const dy = (event.clientY - d.startY) / rect.height * 100;
    let { x, y, w, h } = d.start;
    const right = x + w, bottom = y + h;
    if (d.handle === 'move') { x = clamp(x + dx, 0, 100 - w); y = clamp(y + dy, 0, 100 - h); }
    else {
      const nx = d.handle === 'nw' || d.handle === 'sw' ? clamp(x + dx, 0, right - 2) : x;
      const ny = d.handle === 'nw' || d.handle === 'ne' ? clamp(y + dy, 0, bottom - 2) : y;
      const nr = d.handle === 'ne' || d.handle === 'se' ? clamp(right + dx, nx + 2, 100) : right;
      const nb = d.handle === 'sw' || d.handle === 'se' ? clamp(bottom + dy, ny + 2, 100) : bottom;
      x = nx; y = ny; w = nr - nx; h = nb - ny;
    }
    onCropChange({ x, y, w, h });
  }, [mirror, onCropChange]);
  const onPointerUp = useCallback(() => { drag.current = null; }, []);

  const handleSx = (handle: Handle) => ({
    position: 'absolute' as const, width: 16, height: 16, bgcolor: '#ffd293', border: '2px solid #172334', borderRadius: '50%', touchAction: 'none',
    cursor: handle === 'nw' || handle === 'se' ? 'nwse-resize' : 'nesw-resize', transform: 'translate(-50%, -50%)',
    left: handle === 'nw' || handle === 'sw' ? 0 : '100%', top: handle === 'nw' || handle === 'ne' ? 0 : '100%',
  });

  return <Dialog open={open} onClose={() => { if (!saving) props.onCancel(); }} maxWidth={props.maxWidth ?? 'md'} fullWidth>
    <DialogTitle>{props.title ?? 'Align north with the printed compass'}</DialogTitle>
    <DialogContent>
      <Typography sx={{ mb: 2 }}>{props.description ?? 'Rotate the arrow until it points the same way as N on this plan. This is saved for this image and used at every viewing angle.'}</Typography>
      <Stack direction={{ xs: 'column', md: props.aside ? 'row' : 'column' }} spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ position: 'relative', textAlign: 'center', bgcolor: '#172231', borderRadius: 1 }}>
            <Box ref={frame} sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}
              onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
              <Box component="img" src={imageUrl} alt="Plan for north calibration" draggable={false}
                sx={{ maxWidth: '100%', maxHeight: '48vh', display: 'block', mx: 'auto', transform: mirror ? 'scaleX(-1)' : 'none', userSelect: 'none' }} />
              {crop && props.onCropChange && <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', transform: mirror ? 'scaleX(-1)' : 'none' }}>
                <Box sx={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 9999px rgba(10,16,28,.55)',
                  clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${crop.x}% ${crop.y}%, ${crop.x}% ${crop.y + crop.h}%, ${crop.x + crop.w}% ${crop.y + crop.h}%, ${crop.x + crop.w}% ${crop.y}%, ${crop.x}% ${crop.y}%)` }} />
                <Box sx={{ position: 'absolute', left: `${crop.x}%`, top: `${crop.y}%`, width: `${crop.w}%`, height: `${crop.h}%`,
                  border: '2px dashed #ffd293', pointerEvents: 'auto', cursor: 'move', touchAction: 'none' }} onPointerDown={onPointerDown('move')}>
                  {(['nw', 'ne', 'sw', 'se'] as Handle[]).map(handle => <Box key={handle} sx={handleSx(handle)} onPointerDown={onPointerDown(handle)} />)}
                </Box>
              </Box>}
              <Box sx={{ position: 'absolute', left: '50%', top: '50%', width: 0, height: 0, transform: `rotate(${value}deg)`, pointerEvents: 'none' }}>
                <Box sx={{ position: 'absolute', bottom: 0, left: -3, width: 6, height: 90, bgcolor: '#ff6a19', boxShadow: '0 0 0 2px white' }} />
                <Box sx={{ position: 'absolute', bottom: 85, left: -15, borderLeft: '15px solid transparent', borderRight: '15px solid transparent', borderBottom: '25px solid #ff6a19' }} />
                <Typography sx={{ position: 'absolute', bottom: 112, left: -12, color: '#ff6a19', bgcolor: '#ffffff', px: 0.5, fontWeight: 900 }}>N</Typography>
              </Box>
            </Box>
          </Box>
          <Slider aria-label="North angle clockwise from image top" min={0} max={359.9} step={0.1} value={value} onChange={(_, v) => props.onChange(v as number)} valueLabelDisplay="auto" />
          <Typography align="center">{value.toFixed(1)}° clockwise from image top · 0° top / 90° right / 180° bottom / 270° left</Typography>
          {props.onMirrorChange && <FormControlLabel sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
            control={<Switch checked={Boolean(mirror)} onChange={(_, v) => props.onMirrorChange?.(v)} />}
            label="Mirror image (this stack is the flipped version of the drawn plan)" />}
          {crop && props.onCropChange && <Typography align="center" sx={{ fontSize: 12, color: 'text.secondary' }}>
            Drag the dashed box so it hugs the drawn unit only · {crop.w.toFixed(0)}% × {crop.h.toFixed(0)}% of the sheet
          </Typography>}
        </Box>
        {props.aside && <Box sx={{ flex: 1, minWidth: 0 }}>{props.aside}</Box>}
      </Stack>
      {props.children}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </DialogContent>
    <DialogActions>
      <Button disabled={saving} onClick={props.onCancel}>Cancel</Button>
      <Button variant="contained" disabled={saving} onClick={props.onConfirm}>{saving ? 'Saving…' : (props.confirmLabel ?? 'Confirm north')}</Button>
    </DialogActions>
  </Dialog>;
}
